import { CellContext } from '@tanstack/react-table';
import { useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { IconButton } from 'src/components/Buttons/IconButton';
import { vars } from 'src/core/themes';
import { Stack } from 'src/core/ui';
import { CopyToClipboardV2 } from 'src/core/ui/CopyToClipboard/CopyToClipboardV2';
import { OpenLinkIcon } from 'src/svgs/OpenLinkIcon';
import { ContentId } from 'src/utils/constants/contentId';

import { BaseCell, BaseCellProps } from './BaseCell';
import * as styles from './LinkCell.css';

export interface LinkCell {
  text: string;
  link: string;
  textOnly?: boolean;
  rightComponent?: React.ReactNode;
}

export interface LinkCell<TData = unknown> extends Omit<
  BaseCellProps,
  'children'
> {
  context?: CellContext<TData, LinkCell>;
}

export function LinkCell<TData>({
  context,
  link,
  rightComponent,
  ...props
}: LinkCell<TData>) {
  const value = context?.getValue() ?? props.text;

  const navigate = useNavigate();

  const onViewClick = useCallback(
    (url: string) => {
      setTimeout(() => {
        navigate(url, {
          state: {
            returnUrl: `${location.pathname}${location.search}`,
          },
        });
      }, 10);
    },
    [navigate]
  );

  if (!value) return <BaseCell {...props} />;

  return (
    <BaseCell
      {...props}
      isFullWidth={true}
      showPadding={false}
      style={{ minWidth: 0, ...props.style }}
    >
      <Stack
        justifyContent="spaceBetween"
        alignItems="center"
        style={{ width: '100%', minWidth: 0 }}
      >
        <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
          <CopyToClipboardV2
            textToCopy={props.text}
            displayCopyIcon={false}
            style={{ minWidth: 0, overflow: 'hidden' }}
            wrapperStyle={{
              display: 'block',
              minWidth: 0,
              overflow: 'hidden',
            }}
          >
            {props.text && (
              <span
                className={styles.text}
                style={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  display: 'block',
                  width: '100%',
                }}
              >
                {props.text}
              </span>
            )}
          </CopyToClipboardV2>
        </div>
        {rightComponent}
        <Link
          className={styles.link}
          to={link}
          style={{ flexShrink: 0 }}
          onClick={(e) => {
            if (e.button === 0 && e.getModifierState('Control') === false) {
              // Perform like a button click when it's a left-click (and no Ctrl)
              // Else the click will be whatever <a /> does
              e.preventDefault();
              e.stopPropagation();

              onViewClick(link);
            }
          }}
        >
          {!props.textOnly && (
            <IconButton
              style={{
                padding: vars.spacing.xxs,
                backgroundColor: vars.color.surfaceSecondary,
              }}
              icon={
                <OpenLinkIcon
                  size={vars.iconSize.s}
                  withHoverEffect
                  fill={vars.color.iconBrand}
                />
              }
              titleContentId={ContentId.ViewDetails}
            />
          )}
        </Link>
      </Stack>
    </BaseCell>
  );
}
